import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Terminal, Home, Mail, User, Code, Layers, Zap } from 'lucide-react';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  targetSection: string;
  command: string;
}

const MobileNavigationMenu: React.FC = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isVisible, setIsVisible] = useState(true); // Always visible on mobile
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [glitchActive, setGlitchActive] = useState(false);

  const menuItems: MenuItem[] = useMemo(() => [
    {
      id: 'hero',
      label: 'Home',
      icon: <Home size={18} />,
      targetSection: 'hero-section',
      command: './home.sh'
    },
    {
      id: 'about',
      label: 'About',
      icon: <User size={18} />,
      targetSection: 'terminal-section',
      command: './about.sh'
    },
    {
      id: 'services',
      label: 'Services',
      icon: <Code size={18} />,
      targetSection: 'tech-showcase',
      command: './services.sh'
    },    {
      id: 'team',
      label: 'DevTeam',
      icon: <Layers size={18} />,
      targetSection: 'team-members-section',
      command: './team.sh'
    },
    {
      id: 'contact',
      label: 'Contact',
      icon: <Mail size={18} />,
      targetSection: 'footer',
      command: './contact.sh'
    }
  ], []);

  // Smooth scroll with parallax effect
  const scrollToSection = useCallback((targetId: string) => {
    const element = document.querySelector(`.${targetId}`) || 
                   document.getElementById(targetId) ||
                   document.querySelector(`[data-section="${targetId}"]`);
    
    if (element) {
      const headerHeight = 100; // Account for header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;

      // Trigger glitch effect
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 300);

      // Close menu after navigation
      setMenuExpanded(false);

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY + 200;
          
          // Determine active section with priority order (most specific first)
          const sectionPriority = [
            { class: 'hero-section', id: 'hero' },
            { class: 'tech-showcase', id: 'services' },  // Check tech-showcase first
            { class: 'terminal-section', id: 'about' },
            { class: 'team-members-section', id: 'team' },
            { class: 'footer', id: 'contact' }
          ];
          
          let foundActiveSection = null;
          
          for (const section of sectionPriority) {
            const element = document.querySelector(`.${section.class}`) || 
                           document.getElementById(section.class);
            if (element) {
              const rect = element.getBoundingClientRect();
              const elementTop = rect.top + window.pageYOffset;
              const elementBottom = elementTop + rect.height;
              
              // For tech-showcase, use more precise detection
              const threshold = section.class === 'tech-showcase' ? 150 : 300;
              
              if (scrollPosition >= elementTop - threshold && scrollPosition < elementBottom - threshold) {
                foundActiveSection = section.id;
                break;
              }
            }
          }
          
          if (foundActiveSection) {
            setActiveSection(foundActiveSection);
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Random glitch effect
  useEffect(() => {
    const glitchInterval = setInterval(() => {
      if (Math.random() < 0.05) { // 5% chance
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 150);
      }
    }, 3000);

    return () => clearInterval(glitchInterval);
  }, []);

  // Handle dropdown toggle
  const toggleMenu = () => {
    setMenuExpanded(!menuExpanded);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.mobile-navigation-menu')) {
        setMenuExpanded(false);
      }
    };

    if (menuExpanded) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [menuExpanded]);

  return (
    <div className={`mobile-navigation-menu ${isVisible ? 'visible' : ''} ${glitchActive ? 'glitch' : ''} ${menuExpanded ? 'expanded' : ''}`}>
      <div className="mobile-menu-container">
        {/* Mobile Menu Header */}
        <div 
          className="mobile-menu-header"
          onClick={toggleMenu}
          onMouseEnter={() => setMenuExpanded(true)}
        >
          <div className="terminal-dots">
            <div className="dot red"></div>
            <div className="dot yellow"></div>
            <div className="dot green"></div>
          </div>
          <div className="mobile-menu-title">
            <Terminal size={16} />
            <span>NAV</span>
          </div>
        </div>

        {/* Mobile Menu Items - Hidden in inactive mode */}
        <div className={`mobile-menu-items ${menuExpanded ? 'visible' : 'hidden'}`}>
          {menuItems.map((item, index) => (
            <div
              key={item.id}
              className={`mobile-menu-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => scrollToSection(item.targetSection)}
              style={{
                animationDelay: `${index * 50}ms`
              }}
            >
              <div className="mobile-menu-item-content">
                <div className="mobile-menu-icon">
                  {item.icon}
                </div>
                <div className="mobile-menu-label">
                  <span className="mobile-menu-text">{item.label}</span>
                  <span className="mobile-menu-command">{item.command}</span>
                </div>
              </div>
              <div className="mobile-menu-item-bg"></div>
              <div className="mobile-menu-item-glow"></div>
            </div>
          ))}
        </div>

        {/* Mobile Menu Footer */}
        <div 
          className="mobile-menu-footer"
          onClick={toggleMenu}
          onMouseEnter={() => setMenuExpanded(true)}
        >
          <div className="mobile-status-indicator">
            <Zap size={12} />
            <span>{menuExpanded ? 'ONLINE' : 'OFFLINE'}</span>
          </div>
        </div>

        {/* Holographic overlay */}
        <div className="mobile-holographic-overlay"></div>
      </div>
    </div>
  );
};

export default MobileNavigationMenu;
